import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import 'slick-carousel/slick/slick.css'
import Slider from 'react-slick'
import throttle from 'lodash/throttle'
import { i18n as I18n } from 'builder/utils/i18n'
import Icon24 from 'builder/components/Icon'
import { withMediaQueries } from 'builder/components/MediaQueries'
import { DocumentTypes } from 'builder/modules/constants'
import { Spinner } from 'builder/components/Spinner'
import Tooltip from 'builder/components/Tooltip'
import SliderProgressBar from 'builder/components/SliderProgressBar'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import {
  Content,
  TemplatesContent,
  TemplatesContainer,
  Template,
  TemplatePreviewContainer,
  TemplatePreview,
  TemplateBadge,
  TemplatesLeftButton,
  TemplatesRightButton,
  PlanBadges,
  FormatBadges,
  Button,
  ClickableArea,
  TemplatePreviewWhite,
  TitleContainer,
  Title,
  Subtitle,
  SpinnerWrapper,
  TemplateNameContainer,
  TemplateName,
  TemplateNameText,
  TemplateUsageNumber,
  ProgressBarWrapper,
  ProgressBarContainer,
  TemplateContainer,
  TemplatesWrapper,
  TemplatePreviewMobile,
  TemplateSelectionMobileContainer,
  ClickableAreaMobile,
  ButtonMobile,
} from './styles'
import templateHeightFactorSelector from 'builder/components/Helper/utils/templateHeightFactorSelector'
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperClass } from 'swiper'

const DEFAULT_WIDTH = 420
const DEFAULT_HEIGHT = 560

const SIDE_PADDING_PHONE = 32
const PHONE_MAX_SIZE = 560

const CURRENT_TEMPLATE_SCALE_FACTOR = 1.04

function Templates({
  templates,
  onChoose,
  mediaQueries,
  recommended = 'stockholm',
  currentTemplate = '',
  documentType,
}) {
  const templatesRef = useRef(null)
  const templateRef = useRef(null)
  const sliderRef = useRef(null)
  const swipingRef = useRef(false)
  const componentMountedRef = useRef(true)

  const getInitialSlideIndex = useCallback(() => {
    const initialSlide = currentTemplate || recommended
    const index = templates.findIndex(item => item.id === initialSlide)
    return index >= 0 ? index : 0
  }, [templates, recommended, currentTemplate])

  // eslint-disable-next-line prettier/prettier
  const templatesGallery = useRef<SwiperClass | null>(null)
  // eslint-disable-next-line prettier/prettier
  const templateListRef = useRef<HTMLDivElement | null>(null)
  const [templateWidth, setTemplateWidth] = useState(DEFAULT_WIDTH)
  const [templateHeight, setTemplateHeight] = useState(DEFAULT_HEIGHT)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(getInitialSlideIndex)
  const [nextSlideIndex, setNextSlideIndex] = useState(getInitialSlideIndex)
  const [templateImages, setTemplateImages] = useState({})
  const { isPhone } = useMediaQueries()
  useEffect(() => {
    if (templates.length) {
      const initialSlide = getInitialSlideIndex()
      setCurrentSlideIndex(initialSlide)
    }
  }, [getInitialSlideIndex, templates.length])

  const templateMargins = { top: 70, bottom: 98 }

  const visibleSlideIndexes = useMemo(() => {
    if (!templates.length) return []

    const circularIndex = offset =>
      (currentSlideIndex + offset + templates.length) % templates.length

    if (mediaQueries.isPhone) {
      return [circularIndex(-1), currentSlideIndex, circularIndex(1)]
    } else {
      return [
        circularIndex(-2),
        circularIndex(-1),
        currentSlideIndex,
        circularIndex(1),
        circularIndex(2),
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex])

  const loadImagesFor = useCallback(
    (templatesToLoad, { lowRes = false, immediate = false } = {}) => {
      const promises = templatesToLoad.map(template => {
        if (templateImages[template?.id]) {
          return Promise.resolve()
        }
        const url = lowRes ? template?.thumbnailLowRes : template?.thumbnail
        const imageLoadPromise = immediate ? Promise.resolve() : failsafeLoadImg(url)
        return imageLoadPromise.then(() => {
          if (!componentMountedRef.current) return
          setTemplateImages(prevImages => ({
            ...prevImages,
            [template?.id]: url,
          }))
        })
      })
      return Promise.all(promises)
    },
    [templateImages],
  )

  const loadTemplateImages = useCallback(() => {
    const visibleTemplates = visibleSlideIndexes.map(index => templates[index])
    componentMountedRef.current = true

    loadImagesFor(visibleTemplates, { lowRes: true })
      .then(() => setIsLoaded(true))
      .then(() => loadImagesFor(visibleTemplates))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSlideIndexes])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    throttle(() => {
      const templatesElt = templatesRef.current
      if (!templatesElt) return
      const { top } = templatesElt.getBoundingClientRect()
      let newWidth = 0
      let newHeight = 0
      const pdfHeightFactor = templateHeightFactorSelector(currentTemplate)

      if (window.innerWidth < PHONE_MAX_SIZE) {
        newWidth = window.innerWidth - SIDE_PADDING_PHONE * 2
        newHeight = newWidth / pdfHeightFactor
      } else {
        newHeight = Math.max(
          300,
          window.innerHeight - top - templateMargins.top - templateMargins.bottom,
        )
        newWidth = newHeight * pdfHeightFactor
      }

      setTemplateHeight(newHeight)
      setTemplateWidth(newWidth)
    }, 50),
    [currentTemplate],
  )

  const handlePreviousSlideClick = useCallback(() => {
    sliderRef.current && sliderRef.current.slickPrev()
  }, [])

  const handleNextSlideClick = useCallback(() => {
    sliderRef.current && sliderRef.current.slickNext()
  }, [])

  const handleKeyDown = useCallback(
    event => {
      const key = event.which || event.keyCode
      switch (key) {
        case 13: {
          const template = templates[currentSlideIndex]
          if (template) {
            onChoose(template.id)
          }
          break
        }
        case 37:
          handlePreviousSlideClick()
          break
        case 39:
          handleNextSlideClick()
          break
        default:
          break
      }
    },
    [handlePreviousSlideClick, handleNextSlideClick, templates, currentSlideIndex, onChoose],
  )

  const handleChooseClick = useCallback(
    template => {
      onChoose(template.id)
      trackInternalEvent(`choose_sign_up_${documentType}_template`, {
        template: template.id,
        label: 'funnel',
      })
      trackMarketingEvent('Sign Up', template.name)
    },
    [onChoose, documentType],
  )

  const handleMouseDown = useCallback(event => {
    event.preventDefault()
  }, [])

  const handleMouseUp = useCallback(() => {
    if (sliderRef.current && sliderRef.current.innerSlider) {
      swipingRef.current = sliderRef.current.innerSlider.state.swiping
    }
  }, [])

  const handleClickCapture = useCallback(event => {
    if (swipingRef.current) {
      event.stopPropagation()
    }
  }, [])

  const handleChangeSlide = useCallback((current, next) => {
    setNextSlideIndex(next)
  }, [])

  const handleAfterChangeSlide = useCallback(current => {
    trackInternalEvent('slide_sign_up_templates')
    setCurrentSlideIndex(current)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    document.addEventListener('keydown', handleKeyDown)

    requestAnimationFrame(() => {
      handleResize()
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('keydown', handleKeyDown)
      componentMountedRef.current = false
    }
  }, [handleResize, handleKeyDown, loadTemplateImages, templates, documentType])

  useEffect(() => {
    if (templates.length) {
      loadTemplateImages()
    }
  }, [templates, currentSlideIndex, loadTemplateImages])

  useEffect(() => {
    if (window.location.pathname === '/app/create-resume/templates') {
      trackMarketingEvent('Application', `visit_app_${documentType}_templates_page`)
      trackInternalEvent(`visit_app_${documentType}_templates_page`)
    } else if (window.location.pathname === '/app/create-cover-letter/templates') {
      trackInternalEvent(`visit_app_${documentType}_templates_page`)
    }
  }, [documentType])

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 300,
    slidesToShow: mediaQueries.isDesktop ? 3 : 1,
    slidesToScroll: 1,
    centerMode: true,
    focusOnSelect: true,
    initialSlide: getInitialSlideIndex(),
    variableWidth: true,
    adaptiveHeight: true,
    swipeToSlide: true,
    afterChange: handleAfterChangeSlide,
    beforeChange: handleChangeSlide,
  }

  const showHeader = mediaQueries.isTablet
  const badgesVerticalMargin = (templateHeight * CURRENT_TEMPLATE_SCALE_FACTOR - templateHeight) / 2
  const badgesHorizontalMargin = (templateWidth * CURRENT_TEMPLATE_SCALE_FACTOR - templateWidth) / 2

  const handleTemplateChangeMobile = useCallback(
    swiper => {
      const realIndex = swiper?.realIndex ?? 0

      setCurrentSlideIndex(realIndex)
      setNextSlideIndex((realIndex + 1) % templates.length)

      trackInternalEvent('slide_sign_up_templates')
    },
    [templates.length],
  )

  const mobileSwiper = useMemo(() => {
    return (
      <TemplateSelectionMobileContainer ref={templateListRef}>
        <TemplatesWrapper>
          <ReactSwiper
            onSwiper={swiper => (templatesGallery.current = swiper)}
            initialSlide={getInitialSlideIndex()}
            slidesPerView="auto"
            spaceBetween={8}
            centeredSlides={true}
            loop
            onSlideChange={handleTemplateChangeMobile}
          >
            {templates?.map((template, index) => {
              return (
                <SwiperSlide key={template.id}>
                  <TemplateContainer>
                    <TemplateNameContainer>
                      <TemplateName>
                        <TemplateNameText>{template.name}</TemplateNameText>
                        {template.id === recommended && (
                          <Tooltip value={I18n.t('builder.sign_up.most_popular')}>
                            <TemplateBadge />
                          </Tooltip>
                        )}
                      </TemplateName>
                      <TemplateUsageNumber>
                        {I18n.t('builder.sign_up.users_chose_this_template', {
                          number: template.usersChose,
                        })}
                      </TemplateUsageNumber>
                    </TemplateNameContainer>
                    <TemplatePreviewMobile
                      isActive={currentSlideIndex === index}
                      image={templateImages[template.id]}
                    >
                      {currentSlideIndex === index && (
                        <>
                          <ClickableAreaMobile onClick={() => handleChooseClick(template)} />
                          <ButtonMobile onClick={() => handleChooseClick(template)}>
                            {I18n.t('builder.sign_up.select')}
                          </ButtonMobile>
                        </>
                      )}
                    </TemplatePreviewMobile>
                  </TemplateContainer>
                </SwiperSlide>
              )
            })}
          </ReactSwiper>
        </TemplatesWrapper>
      </TemplateSelectionMobileContainer>
    )
  }, [
    currentSlideIndex,
    getInitialSlideIndex,
    handleChooseClick,
    handleTemplateChangeMobile,
    recommended,
    templates,
    templateImages,
  ])

  return (
    <>
      <Content>
        {showHeader && (
          <TitleContainer>
            <Title>{I18n.t('builder.sign_up.templates_title')}</Title>
            <Subtitle>{I18n.t('builder.sign_up.templates_subtitle')}</Subtitle>
          </TitleContainer>
        )}
        <TemplatesContent ref={templatesRef}>
          {isLoaded ? (
            <TemplatesContainer
              badgesVerticalMargin={badgesVerticalMargin}
              badgesHorizontalMargin={badgesHorizontalMargin}
            >
              <TemplatesLeftButton onClick={handlePreviousSlideClick}>
                <Icon24.Chevron />
              </TemplatesLeftButton>
              <TemplatesRightButton onClick={handleNextSlideClick}>
                <Icon24.Chevron />
              </TemplatesRightButton>

              {isPhone ? (
                mobileSwiper
              ) : (
                <Slider {...settings} ref={sliderRef}>
                  {templates.map(template => (
                    <div key={template.id}>
                      <Template
                        style={{ width: templateWidth }}
                        height={templateHeight}
                        key={template.id}
                        ref={templateRef}
                        onMouseDownCapture={handleMouseDown}
                        onMouseUpCapture={handleMouseUp}
                        onClickCapture={handleClickCapture}
                      >
                        <>
                          <TemplatePreviewContainer>
                            {templateImages[template.id] ? (
                              <TemplatePreview
                                alt={`${template.id} template`}
                                src={templateImages[template.id]}
                              />
                            ) : (
                              <TemplatePreviewWhite />
                            )}

                            {!template.premium && <PlanBadges badges={['Free']} />}

                            <FormatBadges badges={template.supportedFormats} />
                          </TemplatePreviewContainer>

                          <TemplateNameContainer>
                            <TemplateName>
                              <TemplateNameText>{template.name}</TemplateNameText>
                              {template.id === recommended && (
                                <Tooltip value={I18n.t('builder.sign_up.most_popular')}>
                                  <TemplateBadge />
                                </Tooltip>
                              )}
                            </TemplateName>
                            <TemplateUsageNumber>
                              {I18n.t('builder.sign_up.users_chose_this_template', {
                                number: template.usersChose,
                              })}
                            </TemplateUsageNumber>
                          </TemplateNameContainer>

                          <ClickableArea onClick={() => handleChooseClick(template)} />

                          <Button onClick={() => handleChooseClick(template)}>
                            {I18n.t('builder.sign_up.select')}
                          </Button>
                        </>
                      </Template>
                    </div>
                  ))}
                </Slider>
              )}

              <ProgressBarWrapper>
                <ProgressBarContainer>
                  <SliderProgressBar currentStep={nextSlideIndex} steps={templates.length} />
                </ProgressBarContainer>
              </ProgressBarWrapper>
            </TemplatesContainer>
          ) : (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          )}
        </TemplatesContent>
      </Content>
    </>
  )
}

/*
 * Loads an image via its URL and returns a Promise.
 * The returned promise always resolves, even if the
 * image fails to load.
 *
 * @param {string} src - an image URL
 * @returns {Promise<string|null>}
 */
export function failsafeLoadImg(src) {
  return new Promise(resolve => {
    const image = new Image()
    image.onload = () => resolve(src)
    image.onerror = e => resolve(null, e)
    image.src = src
  })
}

Templates.propTypes = {
  templates: PropTypes.array.isRequired,
  onChoose: PropTypes.func.isRequired,
  mediaQueries: PropTypes.object.isRequired,
  recommended: PropTypes.string,
  currentTemplate: PropTypes.string,
  documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
}

export default withMediaQueries(Templates)
